import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Clock, Download, SearchX, Trash2, XCircle, HelpCircle } from 'lucide-react';
import { api, baixarArquivo, ErroApi } from '../../api/cliente';
import type { JobDetalhado } from '../../api/tipos';
import {
  Aviso,
  Botao,
  BotaoLink,
  Cartao,
  Definicoes,
  Esqueleto,
  Etiqueta,
  Indicador,
  Indicadores,
  Progresso,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../componentes/ui';
import {
  dataHora,
  estimativaDownload,
  numero,
  plural,
  porcentagem,
} from '../../util/formato';
import { etiquetaDoJob, terminou } from './statusJob';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import css from './DetalheJob.module.css';

const INTERVALO_ATIVO_MS = 4_000;
const VOLTAR = { para: '/painel/downloads', rotulo: 'Downloads' };

export function DetalheJob() {
  const { jobId } = useParams<{ jobId: string }>();
  const navegar = useNavigate();
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [job, setJob] = useState<JobDetalhado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [naoExiste, setNaoExiste] = useState(false);
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let vivo = true;
    let timer: number | undefined;

    async function ciclo() {
      try {
        const j = await api<JobDetalhado>(`/nfe/lote/${jobId}`);
        if (!vivo) return;
        setJob(j);
        if (!terminou(j)) {
          timer = window.setTimeout(() => void ciclo(), INTERVALO_ATIVO_MS);
        }
      } catch (err) {
        if (!vivo) return;
        // 404 aqui também é a resposta para job de outro dono — a API não
        // distingue, de propósito, para não confirmar a existência do id.
        if (err instanceof ErroApi && err.status === 404) setNaoExiste(true);
        else if (err instanceof ErroApi) setErro(err.message);
      }
    }

    void ciclo();
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  async function baixar() {
    setErro(null);
    setBaixando(true);
    try {
      await baixarArquivo(`/nfe/lote/${jobId}/download`, `nfe-lote-${jobId}.zip`);
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível baixar o ZIP.');
    } finally {
      setBaixando(false);
    }
  }

  async function remover() {
    if (!job) return;
    const emAndamento = !terminou(job);
    const ok = await confirmar({
      titulo: emAndamento ? 'Cancelar esta busca?' : 'Apagar esta busca?',
      mensagem: emAndamento
        ? 'Cancelar a busca apaga o progresso e os XMLs já baixados.'
        : 'Apagar esta busca também apaga os XMLs baixados. Se ainda não baixou o ZIP, faça isso antes.',
      confirmar: emAndamento ? 'Cancelar busca' : 'Apagar busca',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;
    try {
      await api<void>(`/nfe/lote/${jobId}`, { metodo: 'DELETE' });
      notificar(emAndamento ? 'Busca cancelada.' : 'Busca apagada.');
      navegar('/painel/downloads', { replace: true });
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível remover a busca.');
    }
  }

  if (naoExiste) {
    return (
      <Pagina largura="estreita">
        <TituloPagina titulo="Busca não encontrada" voltar={VOLTAR} />
        <Cartao padding={0}>
          <Vazio
            icone={<SearchX />}
            titulo="Esta busca não está mais aqui"
            descricao="Ela não existe, já foi apagada pela limpeza automática (48 h) ou pertence a outra conta."
            acao={
              <BotaoLink to="/painel/downloads" aparencia="secundario">
                Voltar para Downloads
              </BotaoLink>
            }
          />
        </Cartao>
      </Pagina>
    );
  }

  if (!job) {
    return (
      <Pagina>
        <TituloPagina titulo={<Esqueleto altura={28} largura={200} />} voltar={VOLTAR} />
        {erro && <Aviso>{erro}</Aviso>}
        <div className={css.carregando}>
          <Esqueleto altura={96} />
          <Esqueleto altura={96} />
          <Esqueleto altura={96} />
          <Esqueleto altura={96} />
        </div>
        <Esqueleto altura={160} />
      </Pagina>
    );
  }

  const etiqueta = etiquetaDoJob(job);
  const andamento = !terminou(job);

  return (
    <Pagina>
      <TituloPagina
        voltar={VOLTAR}
        titulo={`Busca de ${plural(job.total, 'chave')}`}
        descricao={
          <span className={css.subtitulo}>
            <Etiqueta tom={etiqueta.tom} pulsando={etiqueta.pulsando}>
              {etiqueta.texto}
            </Etiqueta>
            <span>Criada em {dataHora(job.createdAt)}</span>
          </span>
        }
        acoes={
          <>
            <Botao
              aparencia="perigo"
              icone={<Trash2 />}
              onClick={() => void remover()}
            >
              {andamento ? 'Cancelar busca' : 'Apagar busca'}
            </Botao>
            <Botao
              icone={<Download />}
              onClick={() => void baixar()}
              carregando={baixando}
              disabled={job.ok === 0}
              title={job.ok === 0 ? 'Nenhum XML baixado ainda' : undefined}
            >
              Baixar ZIP ({plural(job.ok, 'XML', 'XMLs')})
            </Botao>
          </>
        }
      />

      {erro && <Aviso>{erro}</Aviso>}
      {job.error && <Aviso titulo="A busca falhou">{job.error}</Aviso>}

      <Indicadores>
        <Indicador
          rotulo="XMLs baixados"
          icone={<CheckCircle2 />}
          tom={job.ok > 0 ? 'ok' : undefined}
          valor={numero(job.ok)}
          detalhe={`${porcentagem(job.total > 0 ? job.ok / job.total : 0)} das chaves`}
        />
        <Indicador
          rotulo="Não encontradas"
          icone={<HelpCircle />}
          valor={numero(job.notfound)}
          detalhe="Não existem na fonte; não são cobradas"
        />
        <Indicador
          rotulo="Com erro"
          icone={<XCircle />}
          tom={job.erro > 0 ? 'erro' : undefined}
          valor={numero(job.erro)}
          detalhe="Após esgotar as retentativas"
        />
        <Indicador
          rotulo="Na fila"
          icone={<Clock />}
          valor={numero(job.pendentes)}
          detalhe={job.pendentes > 0 ? estimativaDownload(job.pendentes) : 'Nada pendente'}
        />
      </Indicadores>

      <Colunas minimo={360} alinhar="start">
        <Cartao titulo="Progresso">
          <div className={css.progresso}>
            <div className={css.progressoTopo}>
              <strong className="num">{porcentagem(job.progresso)}</strong>
              <span className="num">
                {numero(job.processados)} de {plural(job.total, 'chave processada', 'chaves processadas')}
              </span>
            </div>
            <Progresso
              valor={job.progresso}
              rotulo="Progresso da busca"
              tom={job.erro > 0 ? 'erro' : undefined}
            />
            {job.pendentes > 0 && (
              <p className={css.nota}>
                Faltam {numero(job.pendentes)} — {estimativaDownload(job.pendentes)} pelo ritmo da
                fonte. Pode fechar a aba; a busca continua.
              </p>
            )}
          </div>
        </Cartao>

        <Cartao titulo="Detalhes">
          <Definicoes
            itens={[
              ['Criada', dataHora(job.createdAt)],
              ['Início do processamento', dataHora(job.startedAt)],
              ['Finalizada', dataHora(job.finishedAt)],
              ['Identificador', <code className={css.id}>{job.id}</code>],
            ]}
          />
        </Cartao>
      </Colunas>

      {andamento && (
        <Aviso tom="info">
          A busca continua rodando mesmo com esta aba fechada, e retoma de onde parou se o serviço
          reiniciar. O ZIP pode ser baixado a qualquer momento com o que já foi obtido.
        </Aviso>
      )}
    </Pagina>
  );
}
